import type { ConnectorDefinition } from "./types";
import { isTriviallyMatchingPattern } from "./urlPatternConversion";

type ResolveValue = (
  value: string,
  configuration: Record<string, string>
) => string;

type InitialOAuthConnection = {
  provider: string;
  clientId?: string;
  scopes?: string[];
};

type InitialConnectorValues = {
  name: string;
  urlPattern: string;
  configuration: Record<string, string>;
  createOAuthConnection?: InitialOAuthConnection;
};

function parseScopes(value: string) {
  return value.split(/\s+/).filter((scope) => scope.length > 0);
}

export function getInitialConnectorValues(
  definition: ConnectorDefinition,
  resolveValue: ResolveValue = (value) => value
): InitialConnectorValues {
  const configuration: Record<string, string> = {};

  let name = "";
  let explicitUrlPattern: string | undefined;
  const oauthConnection: InitialOAuthConnection | undefined = definition.oauth
    ? { provider: definition.identifier }
    : undefined;

  for (const step of definition.steps) {
    for (const item of step.items) {
      if (
        (item.kind !== "input" &&
          item.kind !== "url" &&
          item.kind !== "hostname" &&
          item.kind !== "select" &&
          item.kind !== "url-pattern") ||
        !("defaultValue" in item && item.defaultValue)
      ) {
        continue;
      }

      const value = resolveValue(item.defaultValue, configuration);
      const { target } = item;

      if (target.kind === "connectedApp") {
        if (target.field === "name") name = value;
        if (target.field === "urlPattern") explicitUrlPattern = value;
        continue;
      }

      if (target.kind === "configuration") {
        configuration[target.field] = value;
        continue;
      }

      if (!oauthConnection) continue;
      if (target.field === "clientId") oauthConnection.clientId = value;
      if (target.field === "scopes")
        oauthConnection.scopes = parseScopes(value);
    }
  }

  let urlPattern: string;
  if (explicitUrlPattern !== undefined) {
    urlPattern = explicitUrlPattern;
  } else {
    const derived = resolveValue(
      definition.defaultUrlPattern(configuration),
      configuration
    );
    urlPattern = isTriviallyMatchingPattern(derived) ? "" : derived;
  }

  return {
    name,
    urlPattern,
    configuration,
    createOAuthConnection: oauthConnection,
  };
}
